import { ActiveSubscriptionStatuses, PlatformUser, SubscriptionPlan } from 'models/user'
import { UserIsAuth0User, UserIsPlatformUser, UserIsPlatformUserClient } from 'utils/typeguards'
import { getRoles, getRolesFromUser } from 'utils/auth'

import { Auth0User } from 'models/auth'
import { isEmbedRoute } from 'constants/router'
import { logger } from 'utils/logging'
import mixpanel from 'mixpanel-browser'
import { runProvider } from './runProvider'

export const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN
export const MIXPANEL_RECORD_SESSION_PERCENT = parseInt(process.env.REACT_APP_MIXPANEL_RECORD_SESSION_PERCENT ?? '0')
// Build sha injected by CI; the order-lifecycle tracking spec uses it to isolate a bad release per CRM.
export const CRM_INTEGRATION_VERSION = process.env.REACT_APP_SHORT_SHA || 'dev'

export const mixpanelController: {
  instance: typeof mixpanel | undefined
} = {
  instance: undefined,
}

export const trackMixpanelUser = (user: Auth0User | PlatformUser) => {
  if (!user) return
  // Auth0 User
  if (UserIsAuth0User(user)) {
    if (user.email) mixpanelController.instance?.people.set('$email', user.email)
    if (user.name) mixpanelController.instance?.people.set('$name', user.name)
    if (user.picture) mixpanelController.instance?.people.set('$avatar', user.picture)
    if (user.sub) mixpanelController.instance?.identify(user.sub)
    if (user.email) mixpanelController.instance?.alias(user.email, user.sub)
    const roles = getRolesFromUser(user)
    mixpanelController.instance?.people.set('roles', Array.from(roles.all.keys()).join(', '))
    // Platform User
  } else if (UserIsPlatformUser(user)) {
    if (user.email) mixpanelController.instance?.people.set('$email', user.email)
    if (user.name) mixpanelController.instance?.people.set('$name', user.name)
    if (user.phone) mixpanelController.instance?.people.set('$phone', user.phone)
    if (user.country) mixpanelController.instance?.people.set('$country_code', user.country)
    if (user.defaultTimezone) mixpanelController.instance?.people.set('$timezone', user.defaultTimezone)
    // The embed has no Auth0 login to alias the email to the Auth0 id the server events carry (ISSUE-4339).
    const distinctId = isEmbedRoute() ? user.auth0Id : user.email
    if (distinctId) mixpanelController.instance?.identify(distinctId)
    // if (user.id) mixpanelController.instance?.alias(user.id, user.email)
    // if (user.pipedriveId) mixpanelController.instance?.alias(user.pipedriveId, user.email)
    getRoles().then(roles => mixpanelController.instance?.people.set('roles', Array.from(roles.all.keys()).join(', ')))

    if (UserIsPlatformUserClient(user)) {
      const isSubscribed = user.workspaces.some(workspace => workspace.plan !== SubscriptionPlan.PERSONAL && ActiveSubscriptionStatuses.has(workspace.status))
      mixpanelController.instance?.people.set('$isSubscribed', isSubscribed)
    }
  }
}

// Propstack iframe (ISSUE-3212): the Mixpanel cookie is third-party there, so browsers drop it and
// every load restarts the anonymous distinct_id. localStorage is partitioned rather than blocked;
// SameSite=None keeps the cookie path usable where third-party cookies are still allowed, host-scoped
// so the identifier never rides along to other *.bkbn.com hosts. The store is named apart from the
// platform's, which shares this origin, because Mixpanel's persistence upgrade() deletes the other
// backend's entry on every init. Replay stays off until Product/Legal sign off on recording a
// broker's screen inside a partner CRM: fs-mask (the PII convention in fullstory.ts) means nothing
// to Mixpanel's recorder.
const EMBED_CONFIG = {
  persistence: 'localStorage' as const,
  persistence_name: `${MIXPANEL_TOKEN}_embed`,
  cross_site_cookie: true,
  cross_subdomain_cookie: false,
  secure_cookie: true,
  record_sessions_percent: 0,
}

const isLocalStorageAvailable = () => {
  try {
    window.localStorage.setItem('bkbn_mp_probe', '1')
    window.localStorage.removeItem('bkbn_mp_probe')
    return true
  } catch {
    return false
  }
}

export const initMixpanel = (isEmbed: boolean) => {
  if (!MIXPANEL_TOKEN) {
    // Console only: the token is unset in local builds, and Sentry-reporting a permanently missing
    // config once per session is what exhausted the error quota in ISSUE-2943.
    logger.warn('Mixpanel not initialized: REACT_APP_MIXPANEL_TOKEN is not set', undefined, { sentry: false })
    return false
  }

  // init writes to persistence, and in localStorage mode also removes the cookie store, which a
  // sandboxed third-party frame can refuse: an unguarded throw here would abort bootstrap.
  let initialized = false
  runProvider('mixpanel', 'init', () => {
    mixpanel.init(MIXPANEL_TOKEN, {
      // Route data to Mixpanel's EU servers by setting the api_host config property.
      api_host: 'https://api-eu.mixpanel.com',
      record_sessions_percent: MIXPANEL_RECORD_SESSION_PERCENT,
      // Performance
      record_idle_timeout_ms: 900000,       // 15 min (vs default 30 min) - saves storage
      record_max_ms: 3600000,               // 1 hour max (vs default 24 hrs)
      record_min_ms: 5000,                  // Discard sessions < 5 seconds
      record_mask_all_text: false,
      record_block_selector: 'video',

      // Optional features
      record_canvas: true,
      record_heatmap_data: true,

      ...(isEmbed ? EMBED_CONFIG : {}),
    })
    mixpanelController.instance = mixpanel
    initialized = true
  })
  if (!initialized) return false

  // Mixpanel downgrades to cookie persistence without saying so, which is the very case the embed
  // config exists to avoid: without this line missing embed data looks like nothing happened.
  if (isEmbed && !isLocalStorageAvailable()) {
    logger.warn('Mixpanel fell back to cookie persistence in the embed: localStorage is unavailable', undefined, { sentry: false })
  }

  // Super properties per the order-lifecycle tracking spec (ISSUE-2036): crm is the CRM-agnostic
  // origin dimension, render_context separates iframe from standalone. Registered once, they stamp
  // every event, screen_view included, without adding a prop at each shared purchase-flow call site.
  // Today the embed routes serve exactly one CRM; once a second one is embedded, crm must come from
  // the session-exchange context instead (ISSUE-3213 registers the session-scoped dimensions).
  runProvider('mixpanel', 'register', () => mixpanel.register({
    crm: isEmbed ? 'propstack' : 'direct',
    render_context: isEmbed ? 'iframe' : 'standalone',
    crm_integration_version: CRM_INTEGRATION_VERSION,
  }))
  return true
}

/** A no-op before init, like registerMixpanelSuperProperties. */
export const identifyMixpanelUser = (distinctId: string) => {
  runProvider('mixpanel', 'identify', () => mixpanelController.instance?.identify(distinctId))
}

type SuperPropertyValue = string | number | boolean | null | undefined

/**
 * Registers super properties on the initialised instance without re-running init, which rewrites the
 * persistence the embed config protects. The session-scoped dimensions (ISSUE-3213) are only known after
 * the exchange and orderAttemptId (ISSUE-3218) once the purchase flow opens. Null and undefined values are
 * dropped: an unknown dimension must not be registered as null. A no-op before init.
 *
 * Non-persistent by default: these mirror an in-memory session and a single attempt, both re-registered
 * on every page load, whereas Mixpanel's default would keep them in the shared store past a reload, a
 * failed refresh or the card-payment redirect and stamp the next launch with the previous one's values.
 */
export const registerMixpanelSuperProperties = (
  props: Record<string, SuperPropertyValue>,
  { persistent = false }: { persistent?: boolean } = {}
) => {
  const defined = Object.fromEntries(Object.entries(props).filter(([, value]) => value !== null && value !== undefined))
  runProvider('mixpanel', 'register', () => mixpanelController.instance?.register(defined, { persistent }))
}

/**
 * Non-persistent by default, mirroring registerMixpanelSuperProperties: Mixpanel's own unregister defaults to
 * the persistent store and leaves a non-persistent property in place (ISSUE-3899).
 */
export const unregisterMixpanelSuperProperty = (name: string, { persistent = false }: { persistent?: boolean } = {}) => {
  runProvider('mixpanel', 'unregister', () => mixpanelController.instance?.unregister(name, { persistent }))
}
